import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { loadInitiatedTrackProgressCards } from '../../services/achievementsService';
import AchievementTrophyIcon from './AchievementTrophyIcon';
import MasteryBrainIcon from './MasteryBrainIcon';
import IconRenderer from '../ui/IconRenderer';
import UserProfileListPanel from './UserProfileListPanel';
import { UserProfileTracksListSkeleton } from './UserProfilePanelSkeletons';
import useProfileListPagination from './useProfileListPagination';
import {
  formatLanguageDisplay,
  formatRoadmapTrackTitle,
  normalizeRoadmapLanguageKey
} from '../../utils/roadmapDisplay';
import { formatSnippetMasteryCountAria } from '../../constants/snippetMasteryCopy';
import {
  ROADMAP_GRAPH_LANG_SEARCH_PARAM,
  ROADMAP_GRAPH_NAV_FROM_PROFILE_STATE,
  ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY,
  ROADMAP_PATHS
} from '../Roadmap/roadmapPaths';
import { useDirectPremiumCheck } from '../../hooks/useDirectPremiumCheck';
import { userCanAccessRoadmapTrack } from '../../utils/roadmapSubscription';
import './UserAchievementsList.css';

const USER_PROFILE_TRACKS_PAGE_SIZE_STORAGE_KEY = 'flashcode-profile-tracks-page-size';
const USER_PROFILE_ACHIEVEMENTS_LIST_LANG_ICON_STYLE = { width: '35px', height: '35px' };

function languageIconNameFromKey(langKey) {
  if (!langKey) return 'javascript';
  const k = String(langKey).trim().toLowerCase();
  if (k === 'c++' || k === 'cpp' || k === 'cplusplus') return 'cpp';
  if (k === 'c#' || k === 'csharp') return 'csharp';
  return k.replace(/\s+/g, '') || 'javascript';
}

/**
 * One card per initiated learning track, with visible progress (not hidden until completion).
 * When `externalTracks` is true, parent supplies `trackCards` / `tracksLoading` (avoids duplicate fetch).
 */
export default function UserAchievementsList({
  userId,
  languageFilter,
  canView,
  trackCards: trackCardsProp,
  tracksLoading: tracksLoadingProp,
  externalTracks = false
}) {
  const navigate = useNavigate();
  const { isPremiumUser } = useDirectPremiumCheck(userId);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const openTrackRoadmap = useCallback(
    (card) => {
      if (!card?.trackId) return;
      const trackRef = {
        trackId: card.trackId,
        language: card.language,
        title: card.trackTitle
      };
      if (!userCanAccessRoadmapTrack(isPremiumUser, card.trackId, trackRef)) {
        window.dispatchEvent(new CustomEvent('navigateToSubscription'));
        return;
      }
      const langKey = normalizeRoadmapLanguageKey(card.language || 'javascript');
      navigate(
        {
          pathname: ROADMAP_PATHS.graph,
          search: createSearchParams({
            [ROADMAP_GRAPH_LANG_SEARCH_PARAM]: langKey
          }).toString()
        },
        {
          state: {
            ...ROADMAP_GRAPH_NAV_FROM_PROFILE_STATE,
            [ROADMAP_GRAPH_SELECTED_TRACK_ID_STATE_KEY]: card.trackId
          }
        }
      );
    },
    [navigate, isPremiumUser]
  );

  const sourceCards = externalTracks ? (trackCardsProp ?? []) : cards;
  const sourceLoading = externalTracks ? Boolean(tracksLoadingProp) : loading;

  useEffect(() => {
    if (externalTracks) return;
    if (!userId || !canView) {
      setCards([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await loadInitiatedTrackProgressCards(userId);
        if (!cancelled) setCards(list);
      } catch (e) {
        console.error('UserAchievementsList: load failed', e);
        if (!cancelled) setCards([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, canView, externalTracks]);

  const filtered = useMemo(() => {
    if (!languageFilter) return sourceCards;
    return sourceCards.filter((c) => (c.language || 'other') === languageFilter);
  }, [sourceCards, languageFilter]);

  const {
    pageSize,
    currentPage,
    setCurrentPage,
    totalPages,
    rangeStart,
    rangeEnd,
    paginate,
    handlePageSizeChange
  } = useProfileListPagination(filtered.length, {
    storageKey: USER_PROFILE_TRACKS_PAGE_SIZE_STORAGE_KEY,
    resetDeps: [languageFilter, userId]
  });

  const paginatedTracks = useMemo(() => paginate(filtered), [filtered, paginate]);

  if (!canView) {
    return (
      <p className="user-profile-achievements-list__private">
        Profile sections are only visible on your own profile.
      </p>
    );
  }

  const emptyLangLabel =
    languageFilter && languageFilter !== 'other' ? formatLanguageDisplay(languageFilter) : null;

  return (
    <UserProfileListPanel
      className="user-profile-achievements-list-panel"
      ariaLabel="Track progress"
      loading={sourceLoading}
      loadingContent={<UserProfileTracksListSkeleton />}
      empty={!sourceLoading && filtered.length === 0}
      emptyContent={
        <p className="user-profile-achievements-list__empty-text">
          {emptyLangLabel ? (
            <>
              No tracks started in{' '}
              <span className="user-profile-achievements-list__empty-lang">{emptyLangLabel}</span>{' '}
              yet.
            </>
          ) : (
            <>No tracks started yet.</>
          )}
        </p>
      }
      itemCount={filtered.length}
      pageSize={pageSize}
      currentPage={currentPage}
      totalPages={totalPages}
      rangeStart={rangeStart}
      rangeEnd={rangeEnd}
      onPageSizeChange={handlePageSizeChange}
      onPrevPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
      onNextPage={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
      pageSizeSelectId="user-profile-achievements-list-page-size"
      navAriaLabel="Track list pages"
      perPageAriaLabel="Tracks per page"
    >
      <ul className="user-profile-achievements-list" aria-label="Track progress">
        {paginatedTracks.map((c) => {
          const total = c.snippetTotal ?? 0;
          const mastered = c.snippetMastered ?? 0;
          const pct = total > 0 ? Math.round((mastered / total) * 100) : 0;
          const trackTitle = formatRoadmapTrackTitle(c.language, c.trackTitle, {
            language: c.language
          });
          const progressLabel =
            total > 0
              ? formatSnippetMasteryCountAria(mastered, total)
              : 'No snippets in this track yet';

          const itemClass =
            'user-profile-achievements-list__item' +
            (c.isComplete ? ' user-profile-achievements-list__item--complete' : '');

          return (
            <li key={c.trackId} className="user-profile-achievements-list__list-item">
              <button
                type="button"
                className={itemClass}
                onClick={() => openTrackRoadmap(c)}
                aria-label={`Open ${trackTitle} in roadmap`}
              >
                <div className="user-profile-achievements-list__row-main">
                  <span className="user-profile-achievements-list__leading">
                    <span className="user-profile-achievements-list__icon-wrap" aria-hidden>
                      <IconRenderer
                        name={languageIconNameFromKey(c.language)}
                        type="language"
                        size="large"
                        className="user-profile-achievements-list__lang-icon"
                        style={USER_PROFILE_ACHIEVEMENTS_LIST_LANG_ICON_STYLE}
                      />
                    </span>
                    <span className="user-profile-achievements-list__leading-text">
                      <span className="user-profile-achievements-list__title-line">
                        <h3 className="user-profile-achievements-list__title">{trackTitle}</h3>
                        {c.isComplete ? (
                          <AchievementTrophyIcon
                            className="user-profile-achievements-list__title-trophy"
                            aria-label="All snippets mastered in this track"
                          />
                        ) : null}
                      </span>
                      {total > 0 ? (
                        <div className="user-profile-achievements-list__progress-block">
                          <div className="user-profile-achievements-list__progress-row">
                            <div
                              className="user-profile-achievements-list__progress-track"
                              role="progressbar"
                              aria-valuenow={mastered}
                              aria-valuemin={0}
                              aria-valuemax={total}
                              aria-label={progressLabel}
                            >
                              <div
                                className={[
                                  'user-profile-achievements-list__progress-fill',
                                  c.isComplete
                                    ? 'user-profile-achievements-list__progress-fill--complete'
                                    : ''
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span
                              className={[
                                'user-profile-achievements-list__progress-count',
                                mastered > 0
                                  ? 'user-profile-achievements-list__progress-count--has-mastery'
                                  : ''
                              ]
                                .filter(Boolean)
                                .join(' ')}
                            >
                              <MasteryBrainIcon
                                className="user-profile-achievements-list__progress-mastery-icon"
                                aria-hidden
                              />
                              {mastered}/{total}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <p className="user-profile-achievements-list__no-tests">{progressLabel}</p>
                      )}
                    </span>
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </UserProfileListPanel>
  );
}
